import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Shield, ArrowLeft, Users, TrendingUp, DollarSign, XCircle, Search, RefreshCw, Tag, Loader2, Activity, Lock } from 'lucide-react';
import { toast } from 'sonner';
import AdminUnlock from '../components/admin/AdminUnlock';
import PulseBands, { Pulse } from '../components/admin/PulseBands';
import UserHealthTable from '../components/admin/UserHealthTable';
import DeleteUserDialog from '../components/admin/DeleteUserDialog';
import { PRO_PRICE_LABEL, FOUNDING_PRICE_LABEL } from '../pricing';

interface AppUser {
  user_id: string;
  email: string;
  name: string;
  business_name?: string;
  business_type?: string;
  plan: string;
  subscription_status: string;
  onboarding_completed: boolean;
  created_at: string;
  deleted_at?: string;
  purge_after?: string;
}

interface Stats {
  total_users: number;
  pro_users: number;
  free_users: number;
  cancelled_users: number;
  estimated_mrr: number;
}

interface DetectorAdoption {
  sent?: number;
  adopted?: number;
  dismissed?: number;
  adoption_rate?: number;
}

interface EngineHealth {
  last_run?: any;
  briefs_7d?: number;
  briefs_30d?: number;
  send_failures_7d?: number;
  adoption?: Record<string, DetectorAdoption>;
  measured?: { count?: number; credited_total?: number; didnt_pan_out?: number };
  lifetime_found_total?: number;
}

/** Maps engine detector keys to human-friendly labels (mirrors BriefCard). */
const DETECTOR_LABELS: Record<string, string> = {
  promote: 'Rising star',
  decay: 'Fading favorite',
  bundle: 'Bundle opportunity',
  upsell: 'Counter upsell',
  price_creep: 'Cost creep',
  invoice_discrepancy: 'Invoice discrepancy',
  dead_buy: 'Over-ordered',
  margin_leak: 'Margin leak',
  daypart: 'Right item, wrong hours',
  reprice: 'Pricing opportunity',
  drop_rework: 'Menu cleanup',
  anomaly: 'Unusual day',
  staffing: 'Staffing',
  sellout: 'Selling out early',
  reorder: 'Reorder signal',
};

/** Whole-dollar formatter for engine health money values. */
const money = (v: any) => {
  const n = Number(v);
  return '$' + (isFinite(n) ? Math.round(n) : 0).toLocaleString('en-US');
};

/** Renders an adoption rate that may arrive as a 0–1 fraction or a percent. */
const ratePct = (r: any) => {
  const n = Number(r);
  if (!isFinite(n)) return '—';
  return `${Math.round(n <= 1 ? n * 100 : n)}%`;
};

function RunStatusChip({ status }: { status?: string }) {
  const s = (status || 'unknown').toLowerCase();
  const style =
    s === 'success' || s === 'ok' || s === 'completed' || s === 'complete'
      ? 'bg-[#2E6B4F]/10 text-[#2E6B4F]'
      : s === 'failed' || s === 'error'
        ? 'bg-[#B54A4A]/10 text-[#B54A4A]'
        : 'bg-[#F2F0ED] text-[#6B6670]';
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium font-sans capitalize whitespace-nowrap ${style}`}>
      {s}
    </span>
  );
}

type FilterTab = 'all' | 'pro' | 'free' | 'cancelled';
type SortKey = 'name' | 'created_at' | 'status';

function StatCard({ icon, label, value, sub }: { icon: React.ReactNode; label: string; value: string | number; sub?: string }) {
  return (
    <div className="bg-white border border-[#E6E2DE] rounded-[14px] p-5 shadow-sm flex items-start gap-4">
      <div className="w-10 h-10 rounded-full bg-[#4A2D5E]/10 flex items-center justify-center flex-shrink-0">
        {icon}
      </div>
      <div>
        <p className="font-sans text-xs text-[#6B6670] uppercase tracking-wider mb-0.5">{label}</p>
        <p className="font-serif text-2xl text-[#2D2A2E] leading-none">{value}</p>
        {sub && <p className="font-sans text-xs text-[#6B6670] mt-1">{sub}</p>}
      </div>
    </div>
  );
}

function PlanBadge({ status, plan }: { status: string; plan: string }) {
  if (status === 'cancelled') {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium font-sans bg-[#B54A4A]/10 text-[#B54A4A]">
        Cancelled
      </span>
    );
  }
  if (plan === 'pro' || status === 'active') {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium font-sans bg-[#2E6B4F]/10 text-[#2E6B4F]">
        Pro
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium font-sans bg-[#E6E2DE] text-[#6B6670]">
      Free
    </span>
  );
}

const ADMIN_EMAIL = 'tryclearpath@gmail.com';

export default function AdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Guard: immediately redirect anyone who isn't the admin
  useEffect(() => {
    if (user && user.email?.toLowerCase() !== ADMIN_EMAIL) {
      navigate('/sales/overview', { replace: true });
    }
  }, [user, navigate]);

  // The console is gated twice: the admin allowlist (above) says WHO may be
  // here, and the admin password says the visit was intentional. `checked`
  // keeps the lock screen from flashing before we know which state we are in.
  const [gate, setGate] = useState<{ checked: boolean; unlocked: boolean; configured: boolean }>(
    { checked: false, unlocked: false, configured: true },
  );
  const [pulse, setPulse] = useState<Pulse | null>(null);

  const readGate = async () => {
    try {
      const res = await axios.get('/api/admin/session');
      setGate({ checked: true, unlocked: !!res.data?.unlocked, configured: res.data?.configured !== false });
    } catch {
      setGate({ checked: true, unlocked: false, configured: true });
    }
  };

  useEffect(() => { readGate(); }, []);

  const lockConsole = async () => {
    try { await axios.post('/api/admin/lock'); } catch { /* locking is best-effort */ }
    setGate((g) => ({ ...g, unlocked: false }));
    setPulse(null);
  };

  const [users, setUsers] = useState<AppUser[]>([]);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [toggling, setToggling] = useState<string | null>(null);

  // Engine health — fetched independently so a failure never blocks the admin panel
  const [engine, setEngine] = useState<EngineHealth | null>(null);
  const [engineLoading, setEngineLoading] = useState(true);

  useEffect(() => {
    if (!gate.unlocked) return;
    setEngineLoading(true);
    axios.get('/api/admin/engine/health')
      .then((res) => setEngine(res.data && typeof res.data === 'object' ? res.data : null))
      .catch(() => setEngine(null))
      .finally(() => setEngineLoading(false));
  }, [gate.unlocked]);

  // Discount code form state
  const [discountCode, setDiscountCode] = useState('');
  const [discountPercent, setDiscountPercent] = useState(100);
  const [discountMonths, setDiscountMonths] = useState(1);
  const [discountExpiresDays, setDiscountExpiresDays] = useState(0);
  const [discountFounding, setDiscountFounding] = useState(false);
  const [creatingDiscount, setCreatingDiscount] = useState(false);
  const [lastCreatedCode, setLastCreatedCode] = useState<string | null>(null);

  const handleCreateDiscount = async () => {
    setCreatingDiscount(true);
    setLastCreatedCode(null);
    try {
      const res = await axios.post('/api/admin/create-discount', {
        code: discountCode.trim(),
        discount_percent: discountPercent,
        discount_months: discountMonths,
        expires_days: discountExpiresDays,
        founding: discountFounding,
      });
      setLastCreatedCode(res.data.code);
      setDiscountCode('');
      setDiscountFounding(false);
      toast.success(`Code created: ${res.data.code}`);
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to create discount code');
    } finally {
      setCreatingDiscount(false);
    }
  };

  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FilterTab>('all');
  const [sortKey, setSortKey] = useState<SortKey>('created_at');
  const [sortAsc, setSortAsc] = useState(false);

  const fetchData = async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const [usersRes, statsRes, pulseRes] = await Promise.all([
        axios.get('/api/admin/users'),
        axios.get('/api/admin/stats'),
        axios.get('/api/admin/pulse'),
      ]);
      setUsers(usersRes.data.users);
      setStats(statsRes.data);
      setPulse(pulseRes.data);
    } catch (err: any) {
      // 401 means the admin session expired mid-visit — drop back to the lock
      // screen rather than bouncing to the dashboard, which would look like a
      // permissions bug for what is really just an 8-hour timer running out.
      if (err?.response?.status === 401) {
        setGate((g) => ({ ...g, unlocked: false }));
      } else {
        toast.error('Admin access denied');
        navigate('/sales/overview', { replace: true });
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { if (gate.unlocked) fetchData(); }, [gate.unlocked, navigate]);

  const togglePlan = async (u: AppUser) => {
    const isCancelled = u.subscription_status === 'cancelled';
    const isPro = u.plan === 'pro' || u.subscription_status === 'active';
    const newPlan = (!isPro || isCancelled) ? 'pro' : 'free';
    setToggling(u.user_id);
    try {
      await axios.post('/api/admin/update-plan', { user_id: u.user_id, plan: newPlan });
      const newStatus = newPlan === 'pro' ? 'active' : 'free';
      setUsers(prev => prev.map(x => x.user_id === u.user_id
        ? { ...x, plan: newPlan, subscription_status: newStatus }
        : x
      ));
      // Update stats optimistically
      if (stats) {
        const wasProActive = isPro && !isCancelled;
        const wasCancelled = isCancelled;
        if (newPlan === 'pro') {
          setStats({
            ...stats,
            pro_users: stats.pro_users + 1,
            free_users: wasCancelled ? stats.free_users : stats.free_users - 1,
            cancelled_users: wasCancelled ? stats.cancelled_users - 1 : stats.cancelled_users,
            estimated_mrr: (stats.pro_users + 1) * 99,
          });
        } else {
          setStats({
            ...stats,
            pro_users: stats.pro_users - 1,
            free_users: stats.free_users + 1,
            estimated_mrr: (stats.pro_users - 1) * 99,
          });
        }
      }
      toast.success(`${u.email} → ${newPlan.toUpperCase()}`);
    } catch {
      toast.error('Failed to update plan');
    } finally {
      setToggling(null);
    }
  };

  const restoreUser = async (u: AppUser) => {
    try {
      await axios.post(`/api/admin/users/${u.user_id}/restore`);
      setUsers(prev => prev.map(x => x.user_id === u.user_id ? { ...x, deleted_at: undefined, purge_after: undefined } : x));
      toast.success(`${u.email} restored`);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Couldn't restore this account");
    }
  };
  const deletedUsers = users.filter(u => u.deleted_at);

  const filteredUsers = useMemo(() => {
    let result = users.filter(u => !u.deleted_at);

    // Filter by tab
    if (filter === 'pro') result = result.filter(u => (u.plan === 'pro' || u.subscription_status === 'active') && u.subscription_status !== 'cancelled');
    else if (filter === 'free') result = result.filter(u => u.plan !== 'pro' && u.subscription_status !== 'active' && u.subscription_status !== 'cancelled');
    else if (filter === 'cancelled') result = result.filter(u => u.subscription_status === 'cancelled');

    // Search
    const q = search.toLowerCase().trim();
    if (q) {
      result = result.filter(u =>
        u.name?.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q) ||
        u.business_name?.toLowerCase().includes(q) ||
        u.business_type?.toLowerCase().includes(q)
      );
    }

    // Sort
    result.sort((a, b) => {
      let aVal: string;
      let bVal: string;
      if (sortKey === 'name') { aVal = a.name || ''; bVal = b.name || ''; }
      else if (sortKey === 'status') { aVal = a.subscription_status || ''; bVal = b.subscription_status || ''; }
      else { aVal = a.created_at || ''; bVal = b.created_at || ''; }
      return sortAsc ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    });

    return result;
  }, [users, filter, search, sortKey, sortAsc]);

  const tabCounts = useMemo(() => {
    const pro = users.filter(u => (u.plan === 'pro' || u.subscription_status === 'active') && u.subscription_status !== 'cancelled').length;
    const cancelled = users.filter(u => u.subscription_status === 'cancelled').length;
    const free = users.length - pro - cancelled;
    return { all: users.length, pro, free, cancelled };
  }, [users]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortAsc(!sortAsc);
    else { setSortKey(key); setSortAsc(true); }
  };

  if (!gate.checked) {
    return (
      <div className="min-h-screen bg-[#FAF9F7] flex items-center justify-center">
        <Loader2 size={20} className="animate-spin text-[#4A2D5E]" />
      </div>
    );
  }

  if (!gate.unlocked) {
    return (
      <AdminUnlock
        configured={gate.configured}
        onUnlocked={readGate}
        onBack={() => navigate('/sales/overview')}
      />
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="animate-pulse flex flex-col items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-plum/20" />
          <div className="h-3 w-24 bg-[#F2F0ED] rounded" />
        </div>
      </div>
    );
  }

  const tabs: { key: FilterTab; label: string }[] = [
    { key: 'all', label: `All (${tabCounts.all})` },
    { key: 'pro', label: `Pro (${tabCounts.pro})` },
    { key: 'free', label: `Free (${tabCounts.free})` },
    { key: 'cancelled', label: `Cancelled (${tabCounts.cancelled})` },
  ];

  return (
    <div className="min-h-screen bg-[#FAF9F7] p-6 md:p-10">
      <div className="max-w-5xl mx-auto">
        <button onClick={() => navigate('/sales/overview')} className="flex items-center gap-1.5 text-sm text-[#6B6670] hover:text-[#2D2A2E] mb-6 font-sans transition-colors">
          <ArrowLeft size={16} /> Back to Dashboard
        </button>

        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#4A2D5E]/10 flex items-center justify-center">
              <Shield size={20} className="text-[#4A2D5E]" />
            </div>
            <div>
              <h1 className="font-serif text-2xl text-[#2D2A2E]">Admin Panel</h1>
              <p className="font-sans text-sm text-[#6B6670]">Logged in as {user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-5">
            <button
              onClick={() => fetchData(true)}
              disabled={refreshing}
              className="flex items-center gap-2 text-sm font-sans text-[#6B6670] hover:text-[#2D2A2E] transition-colors disabled:opacity-50"
            >
              <RefreshCw size={15} className={refreshing ? 'animate-spin' : ''} />
              Refresh
            </button>
            <button
              onClick={lockConsole}
              className="flex items-center gap-2 text-sm font-sans text-[#6B6670] hover:text-[#2D2A2E] transition-colors"
            >
              <Lock size={15} />
              Lock
            </button>
          </div>
        </div>

        {/* Founder pulse — data in → engine → delivery → value → revenue. */}
        {pulse && <PulseBands pulse={pulse} />}
        {pulse && <UserHealthTable rows={pulse.users || []} />}

        {/* Stats cards */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard
              icon={<Users size={18} className="text-[#4A2D5E]" />}
              label="Total Users"
              value={stats.total_users}
            />
            <StatCard
              icon={<TrendingUp size={18} className="text-[#2E6B4F]" />}
              label="Pro Users"
              value={stats.pro_users}
              sub={`${stats.total_users > 0 ? Math.round((stats.pro_users / stats.total_users) * 100) : 0}% conversion`}
            />
            <StatCard
              icon={<DollarSign size={18} className="text-[#4A2D5E]" />}
              label="Est. MRR"
              value={`$${stats.estimated_mrr}`}
              sub={`${PRO_PRICE_LABEL}/user × pro users`}
            />
            <StatCard
              icon={<XCircle size={18} className="text-[#B54A4A]" />}
              label="Cancelled"
              value={stats.cancelled_users}
            />
          </div>
        )}

        {/* Engine Health */}
        <div className="bg-white border border-[#E6E2DE] rounded-[14px] shadow-sm p-5 mb-8">
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <Activity size={16} className="text-[#4A2D5E]" />
            <h2 className="font-serif text-base text-[#2D2A2E]">Engine Health</h2>
            {!engineLoading && engine && (
              <div className="flex items-center gap-2 ml-auto">
                <span className="font-sans text-xs text-[#6B6670]">Last run</span>
                <RunStatusChip
                  status={typeof engine.last_run === 'string' ? engine.last_run : engine.last_run?.status}
                />
                {typeof engine.last_run === 'object' && engine.last_run?.run_key && (
                  <span className="font-mono text-xs text-[#6B6670] bg-[#FAF9F7] border border-[#E6E2DE] px-2 py-0.5 rounded-[6px]">
                    {String(engine.last_run.run_key)}
                  </span>
                )}
              </div>
            )}
          </div>

          {engineLoading ? (
            <div className="animate-pulse space-y-3">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="h-16 bg-[#F2F0ED] rounded-[10px]" />
                ))}
              </div>
              <div className="h-32 bg-[#F2F0ED] rounded-[10px]" />
            </div>
          ) : !engine ? (
            <p className="font-sans text-sm text-[#6B6670]">
              Engine health unavailable — the endpoint didn't respond.
            </p>
          ) : (
            <>
              {/* Headline metrics */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                {[
                  { label: 'Briefs (7d)', value: String(engine.briefs_7d ?? 0) },
                  { label: 'Briefs (30d)', value: String(engine.briefs_30d ?? 0) },
                  { label: 'Send failures (7d)', value: String(engine.send_failures_7d ?? 0), alert: Number(engine.send_failures_7d) > 0 },
                  { label: 'Lifetime found', value: money(engine.lifetime_found_total), forest: true },
                ].map((m) => (
                  <div key={m.label} className="bg-[#FAF9F7] border border-[#E6E2DE] rounded-[10px] px-4 py-3">
                    <p className="font-sans text-[10px] text-[#6B6670] uppercase tracking-wider mb-0.5">{m.label}</p>
                    <p className={`font-serif text-xl leading-none tabular-nums ${m.alert ? 'text-[#B54A4A]' : m.forest ? 'text-[#2E6B4F]' : 'text-[#2D2A2E]'}`}>
                      {m.value}
                    </p>
                  </div>
                ))}
              </div>

              {/* Per-detector adoption */}
              <div className="mb-5">
                <p className="font-sans text-xs font-medium text-[#6B6670] uppercase tracking-wider mb-2">
                  Adoption by detector
                </p>
                {Object.keys(engine.adoption || {}).length === 0 ? (
                  <p className="font-sans text-sm text-[#6B6670]">No adoption data yet.</p>
                ) : (
                  <div className="border border-[#E6E2DE] rounded-[10px] overflow-hidden">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-[#FAF9F7] border-b border-[#E6E2DE]">
                          <th className="text-left font-sans text-xs font-medium text-[#6B6670] uppercase tracking-wider px-4 py-2">Detector</th>
                          <th className="text-right font-sans text-xs font-medium text-[#6B6670] uppercase tracking-wider px-4 py-2">Sent</th>
                          <th className="text-right font-sans text-xs font-medium text-[#6B6670] uppercase tracking-wider px-4 py-2">Adopted</th>
                          <th className="text-right font-sans text-xs font-medium text-[#6B6670] uppercase tracking-wider px-4 py-2">Rate</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(engine.adoption || {}).map(([detector, a]) => (
                          <tr key={detector} className="border-b border-[#E6E2DE] last:border-0">
                            <td className="px-4 py-2.5 font-sans text-sm text-[#2D2A2E]">
                              {DETECTOR_LABELS[detector] || detector}
                            </td>
                            <td className="px-4 py-2.5 font-sans text-sm text-[#2D2A2E] text-right tabular-nums">{a?.sent ?? 0}</td>
                            <td className="px-4 py-2.5 font-sans text-sm text-[#2D2A2E] text-right tabular-nums">{a?.adopted ?? 0}</td>
                            <td className="px-4 py-2.5 font-sans text-sm font-medium text-[#4A2D5E] text-right tabular-nums">{ratePct(a?.adoption_rate)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Measured outcomes */}
              <div>
                <p className="font-sans text-xs font-medium text-[#6B6670] uppercase tracking-wider mb-2">
                  Measured outcomes
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="bg-[#FAF9F7] border border-[#E6E2DE] rounded-[10px] px-4 py-3">
                    <p className="font-sans text-[10px] text-[#6B6670] uppercase tracking-wider mb-0.5">Measured</p>
                    <p className="font-serif text-xl text-[#2D2A2E] leading-none tabular-nums">{engine.measured?.count ?? 0}</p>
                  </div>
                  <div className="bg-[#FAF9F7] border border-[#E6E2DE] rounded-[10px] px-4 py-3">
                    <p className="font-sans text-[10px] text-[#6B6670] uppercase tracking-wider mb-0.5">Credited</p>
                    <p className="font-serif text-xl text-[#2E6B4F] leading-none tabular-nums">{money(engine.measured?.credited_total)}</p>
                  </div>
                  <div className="bg-[#FAF9F7] border border-[#E6E2DE] rounded-[10px] px-4 py-3">
                    <p className="font-sans text-[10px] text-[#6B6670] uppercase tracking-wider mb-0.5">Didn't pan out</p>
                    <p className="font-serif text-xl text-[#2D2A2E] leading-none tabular-nums">{engine.measured?.didnt_pan_out ?? 0}</p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Discount Code Creator */}
        <div className="bg-white border border-[#E6E2DE] rounded-[14px] shadow-sm p-5 mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Tag size={16} className="text-[#4A2D5E]" />
            <h2 className="font-serif text-base text-[#2D2A2E]">Create Discount Code</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-3">
            <div>
              <label className="block font-sans text-xs text-[#6B6670] mb-1">Code <span className="text-[#7C7589]">(blank = auto)</span></label>
              <input
                type="text"
                value={discountCode}
                onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                placeholder="e.g. LAUNCH50"
                className="w-full bg-[#FAF9F7] border border-[#E6E2DE] focus:ring-2 focus:ring-[#4A2D5E]/20 focus:border-[#4A2D5E] rounded-[8px] h-9 font-sans text-sm px-3 outline-none transition-all tracking-wider"
              />
            </div>
            <div>
              <label className="block font-sans text-xs text-[#6B6670] mb-1">Discount %</label>
              <input
                type="number"
                min={1}
                max={100}
                value={discountPercent}
                onChange={(e) => setDiscountPercent(Number(e.target.value))}
                className="w-full bg-[#FAF9F7] border border-[#E6E2DE] focus:ring-2 focus:ring-[#4A2D5E]/20 focus:border-[#4A2D5E] rounded-[8px] h-9 font-sans text-sm px-3 outline-none transition-all"
              />
            </div>
            <div>
              <label className="block font-sans text-xs text-[#6B6670] mb-1">For how many months <span className="text-[#7C7589]">(1 = first month)</span></label>
              <input
                type="number"
                min={1}
                max={24}
                value={discountMonths}
                onChange={(e) => setDiscountMonths(Math.max(1, Number(e.target.value)))}
                disabled={discountPercent === 100 || discountFounding}
                title={discountPercent === 100 ? '100% is always "first month free"' : discountFounding ? 'Founding rate is for life' : undefined}
                className="w-full bg-[#FAF9F7] border border-[#E6E2DE] focus:ring-2 focus:ring-[#4A2D5E]/20 focus:border-[#4A2D5E] rounded-[8px] h-9 font-sans text-sm px-3 outline-none transition-all disabled:opacity-50"
              />
            </div>
            <div>
              <label className="block font-sans text-xs text-[#6B6670] mb-1">Expires in days <span className="text-[#7C7589]">(0 = never)</span></label>
              <input
                type="number"
                min={0}
                value={discountExpiresDays}
                onChange={(e) => setDiscountExpiresDays(Number(e.target.value))}
                className="w-full bg-[#FAF9F7] border border-[#E6E2DE] focus:ring-2 focus:ring-[#4A2D5E]/20 focus:border-[#4A2D5E] rounded-[8px] h-9 font-sans text-sm px-3 outline-none transition-all"
              />
            </div>
          </div>
          <label className="flex items-center gap-2 mb-3 font-sans text-sm text-[#2D2A2E] cursor-pointer">
            <input
              type="checkbox"
              checked={discountFounding}
              onChange={(e) => setDiscountFounding(e.target.checked)}
              className="accent-[#4A2D5E]"
            />
            Founding-shop rate
            <span className="text-xs text-[#6B6670]">({FOUNDING_PRICE_LABEL}/mo locked for life instead of {PRO_PRICE_LABEL}; ignores Discount %)</span>
          </label>
          <div className="flex items-center gap-3">
            <button
              onClick={handleCreateDiscount}
              disabled={creatingDiscount}
              className="flex items-center gap-2 bg-[#4A2D5E] text-white hover:bg-[#3D254D] rounded-[8px] font-sans font-medium text-sm px-4 py-2 transition-colors disabled:opacity-50"
            >
              {creatingDiscount ? <Loader2 size={14} className="animate-spin" /> : <Tag size={14} strokeWidth={1.5} />}
              {creatingDiscount ? 'Creating…' : 'Create Code'}
            </button>
            {lastCreatedCode && (
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm font-semibold text-[#2E6B4F] bg-[#2E6B4F]/10 px-3 py-1.5 rounded-[6px] tracking-widest">
                  {lastCreatedCode}
                </span>
                <button
                  onClick={() => { navigator.clipboard.writeText(lastCreatedCode); toast.success('Copied!'); }}
                  className="font-sans text-xs text-[#6B6670] hover:text-[#2D2A2E] transition-colors"
                >
                  Copy
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Search + filter row */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6670]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, email, or business…"
              className="w-full bg-white border border-[#E6E2DE] focus:ring-2 focus:ring-[#4A2D5E]/20 focus:border-[#4A2D5E] rounded-[8px] h-10 font-sans text-sm pl-9 pr-3 outline-none transition-all"
            />
          </div>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-1 mb-4 border-b border-[#E6E2DE]">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              className={`px-4 py-2 font-sans text-sm font-medium transition-colors border-b-2 -mb-px ${
                filter === tab.key
                  ? 'border-[#4A2D5E] text-[#4A2D5E]'
                  : 'border-transparent text-[#6B6670] hover:text-[#2D2A2E]'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Users table */}
        <div className="bg-white border border-[#E6E2DE] rounded-[14px] overflow-hidden shadow-sm">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#E6E2DE] bg-[#FAF9F7]">
                <th
                  className="text-left font-sans text-xs font-medium text-[#6B6670] uppercase tracking-wider px-5 py-3 cursor-pointer hover:text-[#2D2A2E] select-none"
                  onClick={() => handleSort('name')}
                >
                  User {sortKey === 'name' ? (sortAsc ? '↑' : '↓') : ''}
                </th>
                <th
                  className="text-left font-sans text-xs font-medium text-[#6B6670] uppercase tracking-wider px-5 py-3 cursor-pointer hover:text-[#2D2A2E] select-none hidden sm:table-cell"
                  onClick={() => handleSort('created_at')}
                >
                  Joined {sortKey === 'created_at' ? (sortAsc ? '↑' : '↓') : ''}
                </th>
                <th
                  className="text-left font-sans text-xs font-medium text-[#6B6670] uppercase tracking-wider px-5 py-3 cursor-pointer hover:text-[#2D2A2E] select-none"
                  onClick={() => handleSort('status')}
                >
                  Status {sortKey === 'status' ? (sortAsc ? '↑' : '↓') : ''}
                </th>
                <th className="text-right font-sans text-xs font-medium text-[#6B6670] uppercase tracking-wider px-5 py-3">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-5 py-10 text-center font-sans text-sm text-[#6B6670]">
                    No users match your search
                  </td>
                </tr>
              ) : filteredUsers.map(u => {
                const isPro = (u.plan === 'pro' || u.subscription_status === 'active') && u.subscription_status !== 'cancelled';
                const isCancelled = u.subscription_status === 'cancelled';
                const initials = (u.name || u.email || '?')[0].toUpperCase();

                return (
                  <tr key={u.user_id} className="border-b border-[#E6E2DE] last:border-0 hover:bg-[#FAF9F7]/50 transition-colors">
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#4A2D5E]/15 flex items-center justify-center flex-shrink-0">
                          <span className="font-sans text-xs font-semibold text-[#4A2D5E]">{initials}</span>
                        </div>
                        <div>
                          <div className="font-sans text-sm font-medium text-[#2D2A2E]">{u.name}</div>
                          <div className="font-sans text-xs text-[#6B6670]">{u.email}</div>
                          {u.business_name && (
                            <div className="font-sans text-xs text-[#7C7589]">{u.business_name}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4 font-sans text-sm text-[#6B6670] hidden sm:table-cell">
                      {new Date(u.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-5 py-4">
                      <PlanBadge status={u.subscription_status} plan={u.plan} />
                    </td>
                    <td className="px-5 py-4 text-right whitespace-nowrap">
                      <button
                        onClick={() => setDeleteTarget(u.user_id)}
                        className="font-sans text-xs text-[#7C7589] hover:text-[#B54A4A] mr-3 transition-colors"
                      >
                        Delete…
                      </button>
                      {isCancelled ? (
                        <button
                          onClick={() => togglePlan(u)}
                          disabled={toggling === u.user_id}
                          className="font-sans text-xs font-medium px-3 py-1.5 rounded-full bg-[#2E6B4F]/10 text-[#2E6B4F] hover:bg-[#2E6B4F]/20 transition-colors disabled:opacity-50"
                        >
                          {toggling === u.user_id ? '…' : 'Reactivate Pro'}
                        </button>
                      ) : isPro ? (
                        <button
                          onClick={() => togglePlan(u)}
                          disabled={toggling === u.user_id}
                          className="font-sans text-xs font-medium px-3 py-1.5 rounded-full bg-[#B54A4A]/10 text-[#B54A4A] hover:bg-[#B54A4A]/20 transition-colors disabled:opacity-50"
                        >
                          {toggling === u.user_id ? '…' : 'Downgrade'}
                        </button>
                      ) : (
                        <button
                          onClick={() => togglePlan(u)}
                          disabled={toggling === u.user_id}
                          className="font-sans text-xs font-medium px-3 py-1.5 rounded-full bg-[#2E6B4F]/10 text-[#2E6B4F] hover:bg-[#2E6B4F]/20 transition-colors disabled:opacity-50"
                        >
                          {toggling === u.user_id ? '…' : 'Grant Pro'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <p className="mt-4 font-sans text-xs text-[#7C7589] text-right">
          Showing {filteredUsers.length} of {users.length - deletedUsers.length} users
        </p>

        {deletedUsers.length > 0 && (
          <div className="mt-8">
            <h3 className="font-serif text-base text-[#2D2A2E] mb-3">Recently deleted</h3>
            <div className="bg-white border border-[#E6E2DE] rounded-[14px] divide-y divide-[#E6E2DE]">
              {deletedUsers.map(u => (
                <div key={u.user_id} className="flex items-center justify-between px-5 py-3">
                  <div>
                    <div className="font-sans text-sm text-[#2D2A2E]">{u.email}</div>
                    <div className="font-sans text-xs text-[#7C7589]">
                      Removed for good {u.purge_after ? new Date(u.purge_after).toLocaleDateString() : 'soon'}
                    </div>
                  </div>
                  <button
                    onClick={() => restoreUser(u)}
                    className="font-sans text-xs font-medium px-3 py-1.5 rounded-full bg-[#2E6B4F]/10 text-[#2E6B4F] hover:bg-[#2E6B4F]/20 transition-colors"
                  >
                    Restore
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {deleteTarget && (
          <DeleteUserDialog
            userId={deleteTarget}
            onClose={() => setDeleteTarget(null)}
            onDeleted={(purgeAfter) => {
              setUsers(prev => prev.map(x => x.user_id === deleteTarget
                ? { ...x, deleted_at: new Date().toISOString(), purge_after: purgeAfter } : x));
              setDeleteTarget(null);
            }}
          />
        )}
      </div>
    </div>
  );
}
